import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Wallet, User, RefreshCw, Loader2 } from 'lucide-react';
import { apiService } from '@/services/apiService';
import { authService } from '@/features/auth/services/authService';
import { playClickSound, playSound, isSoundEnabled, SOUND_NAMES } from '@/utils/soundUtils';
import { BALANCE_REFRESH_EVENT } from '@/utils/balanceRefresh';
import { getTierConfig } from '@/constants/tierConfig';
import { useDomainType } from '@/hooks/useDomainType';
import KioskNotificationBell from '@/components/KioskNotificationBell';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import SignupBonusBadge from '@/features/bonus/SignupBonusBadge';
import { useTranslation } from 'react-i18next';

export interface HeaderProps {
  back?: boolean;
  /** When set, Back button navigates to this path instead of history (-1). */
  backTo?: string;
  type: 'logged' | 'normal';
  size?: 'xs' | 'm' | 'l' | 'XL' | 's';
  /** When true, header fits 80px height (smaller padding, logo, title). Used on Home. */
  compact?: boolean;
  className?: string;
  title?: string;
  subtitle?: string;
  /** Show "Welcome back" + user avatar in left section (logged only). Default true. */
  showUserInfo?: boolean;
  /** Show balance box in right section (logged only). Default true. */
  showBalance?: boolean;
  /** Optional custom right slot (e.g. EDIT button). When set, replaces Balance in right section. */
  rightSlot?: React.ReactNode;
  /** When true with type normal, show admin broadcast bell (e.g. logged-in user on home). Logged type always shows the bell. */
  showNotificationBell?: boolean;
}

/**
 * Unified Header component based on Figma design
 * Supports both logged and normal types with multiple sizes
 */
export const Header: React.FC<HeaderProps & React.HTMLAttributes<HTMLElement>> = ({
  back = true,
  backTo,
  type = 'normal',
  size = 'XL',
  compact = false,
  className = '',
  title,
  subtitle,
  showUserInfo = true,
  showBalance = true,
  rightSlot,
  showNotificationBell = false,
  style,
  ...restProps
}) => {
  const { t } = useTranslation();
  const showBell = type === 'logged' || showNotificationBell;
  const navigate = useNavigate();
  const [balance, setBalance] = useState<number>(0);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceDelta, setBalanceDelta] = useState<number | null>(null);
  const [showBalanceAttention, setShowBalanceAttention] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [userTier, setUserTier] = useState<string | null>(null);
  const previousBalanceRef = useRef<number | null>(null);
  const balanceAttentionTimeoutRef = useRef<number | null>(null);
  const lastBalanceSoundAtRef = useRef<number>(0);
  const domainType = useDomainType();

  const applyBalanceWithAttention = (nextBalance: number) => {
    const previous = previousBalanceRef.current;
    setBalance(nextBalance);
    previousBalanceRef.current = nextBalance;

    if (previous === null) return;
    const delta = Number((nextBalance - previous).toFixed(2));
    if (Math.abs(delta) < 0.01) return;

    setBalanceDelta(delta);
    setShowBalanceAttention(true);
    if (balanceAttentionTimeoutRef.current) {
      window.clearTimeout(balanceAttentionTimeoutRef.current);
    }
    balanceAttentionTimeoutRef.current = window.setTimeout(() => {
      setShowBalanceAttention(false);
      setBalanceDelta(null);
    }, 2200);

    const now = Date.now();
    if (isSoundEnabled() && now - lastBalanceSoundAtRef.current > 2500) {
      playSound(delta > 0 ? SOUND_NAMES.MONEY_UP : SOUND_NAMES.MONEY_DOWN);
      lastBalanceSoundAtRef.current = now;
    }
  };

  // Load balance - only for logged type when showBalance is true
  useEffect(() => {
    if (type === 'normal' || !showBalance) {
      if (type === 'normal') setBalance(0);
      setBalanceLoading(false);
      return;
    }

    const loadBalance = async () => {
      try {
        setBalanceLoading(true);
        const response = await apiService.get<{
          balance: number;
          currency: string;
          lastUpdated: string;
        }>('/payment/balance', true);

        if (response.success && response.data) {
          const data = response.data as any;
          const balanceValue = data.balance ?? data.data?.balance ?? 0;
          applyBalanceWithAttention(balanceValue);
        }
      } catch (error) {
        console.error('Error loading balance:', error);
      } finally {
        setBalanceLoading(false);
      }
    };

    loadBalance();
  }, [type, showBalance]);

  // When another part of the app refreshes balance (e.g. Wallet page), update header balance too
  useEffect(() => {
    if (type !== 'logged' || !showBalance) return;

    const onBalanceShouldRefresh = async () => {
      try {
        setBalanceLoading(true);
        const response = await apiService.get<{
          balance: number;
          currency: string;
          lastUpdated: string;
        }>('/payment/balance', true, true);

        if (response.success && response.data) {
          const data = response.data as any;
          const balanceValue = data.balance ?? data.data?.balance ?? 0;
          applyBalanceWithAttention(balanceValue);
        }
      } catch (error) {
        console.error('Error refreshing header balance:', error);
      } finally {
        setBalanceLoading(false);
      }
    };

    window.addEventListener(BALANCE_REFRESH_EVENT, onBalanceShouldRefresh);
    return () => window.removeEventListener(BALANCE_REFRESH_EVENT, onBalanceShouldRefresh);
  }, [type, showBalance]);

  // Load user (name + email) and tier for logged type when showUserInfo is true
  // Tier (gamification) is mobile-domain only — kiosk does not call /auth/tier
  // Uses data.user.profile.firstName / data.user.profile.lastName when available (GET /users/profile shape)
  useEffect(() => {
    if (type === 'logged' && showUserInfo) {
      const loadUser = async () => {
        try {
          const tierPromise =
            domainType === 'mobile' ? authService.getTier() : Promise.resolve(null);
          const [resp, tier] = await Promise.all([authService.getCurrentUser(), tierPromise]);
          if (domainType === 'mobile' && tier) setUserTier(tier);
          else setUserTier(null);
          if (resp.success && resp.data) {
            const userData = resp.data;
            const user = userData.user || userData;
            const email = user.email || userData.email || '';
            const firstName = (user.profile?.firstName ?? user.firstName ?? '').trim();
            const lastName = (user.profile?.lastName ?? user.lastName ?? '').trim();
            const nameFromProfile = [firstName, lastName].filter(Boolean).join(' ').trim();
            let name = (user.name || user.displayName || userData.name || userData.displayName || nameFromProfile || '').trim();
            if (!name) {
              const profileResp = await apiService.getUserProfile();
              if (profileResp.success && profileResp.data?.user?.profile) {
                const p = profileResp.data.user.profile;
                const fn = (p.firstName ?? '').trim();
                const ln = (p.lastName ?? '').trim();
                name = [fn, ln].filter(Boolean).join(' ').trim();
              }
            }
            setUserEmail(email);
            setUserName(name);
          }
        } catch (error) {
          console.error('Error loading user:', error);
        }
      };
      loadUser();
    } else {
      setUserTier(null);
    }
  }, [type, showUserInfo, domainType]);

  const handleReloadBalance = async () => {
    playClickSound();
    try {
      setBalanceLoading(true);
      const response = await apiService.get<{
        balance: number;
        currency: string;
        lastUpdated: string;
      }>('/payment/balance', true, true);

      if (response.success && response.data) {
        const data = response.data as any;
        const balanceValue = data.balance ?? data.data?.balance ?? 0;
        applyBalanceWithAttention(balanceValue);
      }
    } catch (error) {
      console.error('Error reloading balance:', error);
    } finally {
      setBalanceLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (balanceAttentionTimeoutRef.current) {
        window.clearTimeout(balanceAttentionTimeoutRef.current);
      }
    };
  }, []);

  const handleBack = () => {
    playClickSound();
    if (backTo) {
      navigate(backTo);
    } else {
      navigate(-1);
    }
  };

  const handleWalletClick = () => {
    playClickSound();
    if (type === 'normal') {
      sessionStorage.setItem('login_redirect_to', '/account');
      navigate('/login', { state: { redirectTo: '/account' } });
    } else {
      navigate('/account/finance/wallet');
    }
  };

  const handleUserClick = () => {
    playClickSound();
    if (type === 'logged') {
      navigate('/account/settings');
    } else {
      navigate('/login');
    }
  };

  // Size-based classes - Match Figma design exactly
  const sizeClasses = {
    xs: {
      header: 'h-[70px] px-2 py-2',
      container: 'gap-2',
      backButton: 'px-2 py-1.5 text-xs',
      walletButton: 'px-2 py-1.5 text-[8px]',
      title: 'text-lg',
      subtitle: 'text-[8px]',
      balance: 'px-2 py-1.5 text-xs',
      balanceAmount: 'text-lg',
      userButton: 'w-8 h-8',
    },
    s: {
      header: 'h-[85px] px-8 py-4',
      container: 'gap-3',
      backButton: 'px-3 py-2 text-sm',
      walletButton: 'px-4 py-2 text-sm',
      title: 'text-xl',
      subtitle: 'text-xs',
      balance: 'px-4 py-2 text-sm',
      balanceAmount: 'text-xl',
      userButton: 'w-10 h-10',
    },
    m: {
      header: 'h-[85px] px-8 py-4',
      container: 'gap-3',
      backButton: 'px-3 py-2 text-sm',
      walletButton: 'px-4 py-2 text-sm',
      title: 'text-xl',
      subtitle: 'text-xs',
      balance: 'px-4 py-2 text-sm',
      balanceAmount: 'text-xl',
      userButton: 'w-10 h-10',
    },
    l: {
      header: 'h-[85px] lg:h-[110px]',
      container: 'gap-6',
      backButton: 'px-4 py-2 text-sm',
      walletButton: 'px-6 py-2 text-base',
      title: 'text-3xl ipad:text-2xl lg:text-4xl',
      subtitle: 'text-xs ipad:text-[11px] lg:text-sm',
      balance: 'px-6 py-2 text-sm',
      balanceAmount: 'text-2xl lg:text-3xl',
      userButton: 'w-12 h-12',
      padding: '10px 80px',
    },
    XL: {
      header: 'h-[85px] lg:h-[110px]',
      container: 'gap-6',
      backButton: 'px-4 py-2 text-sm',
      walletButton: 'px-6 py-2 text-base',
      title: 'text-3xl ipad:text-2xl lg:text-4xl',
      subtitle: 'text-xs ipad:text-[11px] lg:text-sm',
      balance: 'px-6 py-2 text-sm',
      balanceAmount: 'text-2xl lg:text-3xl',
      userButton: 'w-12 h-12',
      padding: '10px 80px',
    },
  };

  const sizeConfig = sizeClasses[size];

  // Compact mode: fit 80px height (smaller padding and elements)
  const headerHeightClass = compact ? 'h-full min-h-0' : sizeConfig.header;
  const headerPaddingClass = compact ? 'px-2 sm:px-3 md:px-4 py-1.5 sm:py-2' : 'px-3 sm:px-5 md:px-10 ipad:px-10 lg:px-20 py-2 sm:py-2.5';

  // Get user initial: first letter of name, else first letter of email, else 'U'
  const getUserInitial = () => {
    if (userName) return userName.charAt(0).toUpperCase();
    if (userEmail) return userEmail.charAt(0).toUpperCase();
    return 'U';
  };

  return (
    <header
      className={`
        relative z-10 flex flex-col justify-center overflow-visible
        ${compact ? headerHeightClass : sizeConfig.header}
        ${compact ? headerPaddingClass : `px-3 sm:px-5 md:px-10 ipad:px-10 lg:px-20 py-2 sm:py-2.5 ${size === 'XL' || size === 'l' ? '' : size === 'm' || size === 's' ? 'px-4 sm:px-6 md:px-8 ipad:px-8' : 'px-2 sm:px-3 md:px-4 ipad:px-4'}`}
        ${className}
      `}
      {...restProps}
      style={{
        width: '100%',
        borderBottom: '1px solid rgba(255, 255, 255, 0.10)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        ...(style || {}),
        // Override background to ensure correct color
        background: '#040F29',
      }}
    >
      <div className={`w-full h-full flex flex-row items-center justify-between ${compact ? 'gap-1 sm:gap-1.5 md:gap-2' : 'gap-1.5 sm:gap-2 md:gap-4 ipad:gap-4 lg:gap-6'}`}>
        {/* Left Section */}
        <div className="flex items-center flex-shrink-0" style={{ gap: compact ? '6px' : (size === 'XL' || size === 'l' ? '8px sm:12px md:16px lg:20px xl:24px' : size === 'm' ? '6px sm:8px md:12px' : '4px sm:6px md:8px') }}>
          {/* Back Button - for logged type */}
          {type === 'logged' && back && (size === 'l' || size === 'XL') && (
            <button
              onClick={handleBack}
              className={`flex items-center gap-1.5 sm:gap-2 text-white hover:opacity-80 transition-opacity ${compact ? 'py-0.5' : ''}`}
            >
              <ArrowLeft className={compact ? 'w-4 h-4 sm:w-[18px] sm:h-[18px]' : 'w-5 h-5'} />
              <span className={`${compact ? 'text-[10px] sm:text-xs' : 'text-xs'} font-black uppercase`}>
                {t('common.back')}
              </span>
            </button>
          )}

          {/* Back Button - for smaller logged sizes */}
          {type === 'logged' && ['m', 's', 'xs'].includes(size) && (
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity"
            >
              <ArrowLeft className="w-5 h-5" />
              {['m', 's'].includes(size) && (
                <span className="text-xs font-black uppercase">{t('common.back')}</span>
              )}
            </button>
          )}

          {/* Open My Wallet Button - for normal type */}
          {type === 'normal' && (
            <button
              onClick={handleWalletClick}
              className={`flex items-center justify-center hover:opacity-90 transition-all duration-200 active:scale-95 flex-shrink-0 ${compact ? 'px-2 py-1 sm:px-3 sm:py-1.5 gap-1.5' : 'px-3 py-1.5 sm:px-4 sm:py-2 md:px-6 md:py-2 ipad:px-6 ipad:py-2 gap-2 sm:gap-2.5 md:gap-3 ipad:gap-3'}`}
              style={{
                display: 'flex',
                width: 'auto',
                maxWidth: '226px',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '12px',
                border: '2px solid #10B981',
                background: 'rgba(16, 185, 129, 0.10)',
                boxShadow: '0 0 20px 0 rgba(16, 185, 129, 0.40), 0 0 15px 2px rgba(16, 185, 129, 0.20) inset',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
              }}
            >
              <img
                src="/icons/wallet-icon.svg"
                alt="Wallet"
                className={`flex-shrink-0 ${compact ? 'w-4 h-5 sm:w-5 sm:h-6' : 'w-5 h-6 sm:w-[22px] sm:h-[25px] md:w-[24.02px] md:h-[28px] ipad:w-[24.02px] ipad:h-[28px]'}`}
                style={{
                  objectFit: 'contain'
                }}
              />
              <span
                className={`text-white font-bold whitespace-nowrap tracking-wider leading-tight ${compact ? 'text-[10px] sm:text-xs' : 'text-xs sm:text-sm md:text-base ipad:text-base'}`}
                style={{
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  fontWeight: 700,
                  letterSpacing: '1.2px sm:1.3px md:1.4px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {t('header.openMyWallet')}
              </span>
            </button>
          )}

          {/* User Info - for logged type (config: showUserInfo) */}
          {type === 'logged' && showUserInfo && (
            <div className={`flex items-center flex-shrink-0 ${compact ? 'gap-1.5 sm:gap-2 ml-2 sm:ml-3' : 'gap-3 ml-4 sm:ml-6 md:ml-8'}`}>
              {/* Vertical Divider */}
              <div className={`w-px bg-white/10 flex-shrink-0 ${compact ? 'h-4 sm:h-5' : 'h-6'}`} />

              {/* User Avatar & Info */}
              <div className={`flex items-center ${compact ? 'gap-1.5 sm:gap-2' : 'gap-3'}`}>
                {/* Avatar (click -> settings) + Tier badge at top-right corner (display only) */}
                <div className="relative flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      playClickSound();
                      navigate('/account/settings');
                    }}
                    className={`rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 border border-white/20 flex items-center justify-center cursor-pointer hover:opacity-90 active:scale-95 transition-all ${compact ? 'w-7 h-7 sm:w-8 sm:h-8' : 'w-10 h-10'}`}
                    aria-label="Account settings"
                  >
                    <span className={`text-white font-black ${compact ? 'text-xs sm:text-sm' : 'text-lg'}`}>
                      {getUserInitial()}
                    </span>
                  </button>
                  {userTier && (() => {
                    const config = getTierConfig(userTier) ?? getTierConfig('Silver');
                    if (!config) return null;
                    const { Icon, color } = config;
                    return (
                      <span
                        className="group/tier absolute -top-0.5 -right-0.5 flex items-center justify-center rounded-full ring-2 ring-[#040F29] cursor-default"
                        style={{
                          width: compact ? '14px' : '18px',
                          height: compact ? '14px' : '18px',
                          background: 'rgba(0,0,0,0.5)',
                          border: `1.5px solid ${color}`,
                          minWidth: compact ? '14px' : '18px',
                          minHeight: compact ? '14px' : '18px'
                        }}
                      >
                        <Icon className={compact ? 'w-2 h-2' : 'w-2.5 h-2.5'} style={{ color }} />
                        <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 rounded text-xs font-semibold whitespace-nowrap bg-gray-900/95 border border-white/10 shadow-lg opacity-0 invisible group-hover/tier:opacity-100 group-hover/tier:visible transition-all duration-150 z-[1070]">
                          <span style={{ color: config.color }}>{userTier}</span>
                        </span>
                      </span>
                    );
                  })()}
                </div>

                {/* Welcome Text - hidden on mobile (sm and up only) */}
                <div className="hidden sm:flex flex-col min-w-0">
                  <span className={`text-[#22D3EE] font-black ${compact ? 'text-[9px] sm:text-[10px]' : 'text-xs'}`}>
                    {t('header.welcomeBack')}
                  </span>
                  <span className={`text-white font-black truncate max-w-[120px] md:max-w-[160px] ${compact ? 'text-[10px] sm:text-xs' : 'text-sm'}`}>
                    {userName || userEmail || 'User'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Center Section - Title */}
        {/* For logged type: XL and l sizes - show inline only on large desktops (lg+) */}
        {['XL', 'l'].includes(size) && type === 'logged' && (
          <div className={`flex-1 hidden lg:flex flex-col items-center ${compact ? 'gap-0.5' : 'gap-1 md:gap-1.5'}`}>
            <h1 className={`text-white font-black italic tracking-wide text-center px-2 ${compact ? 'text-base md:text-lg' : sizeConfig.title}`}>
              {title || t('header.accountHub')}
            </h1>
            <div className={`bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 rounded-full ${compact ? 'h-0.5 w-10 md:w-12' : 'h-0.5 md:h-1 w-16 md:w-20 ipad:w-[72px] lg:w-24'}`} />
            <p className={`text-white/60 font-semibold tracking-wider text-center px-2 ${compact ? 'text-[9px] md:text-[10px]' : sizeConfig.subtitle}`}>
              {subtitle || t('header.yourLuckyGamingPortal')}
            </p>
          </div>
        )}
        {/* For normal type (home page): Desktop only (lg+) */}
        {type === 'normal' && (
          <div className={`flex-1 hidden lg:flex flex-col items-center ${compact ? 'gap-0.5' : 'gap-1 md:gap-1.5 ipad:gap-1'}`}>
            <h1 className={`text-white font-black italic tracking-wide text-center px-2 ${compact ? 'text-lg md:text-xl' : sizeConfig.title}`}>
              {t('header.gamingPlatform')}
            </h1>
            <div className={`bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 rounded-full ${compact ? 'h-0.5 w-12 md:w-14' : 'h-0.5 md:h-1 w-16 md:w-20 ipad:w-[72px] lg:w-24'}`} />
            <p className={`text-white/60 font-semibold tracking-wider text-center px-2 ${compact ? 'text-[10px] md:text-xs' : sizeConfig.subtitle}`}>
              {t('header.selectPlatformsToDeposit')}
            </p>
          </div>
        )}

        {/* Right Section - (Bell) (Language) (Balance / rightSlot) */}
        <div className="flex items-center flex-shrink-0 gap-2 pr-5">
          {showBell ? (
            <div data-tour-id="post-login-tour.notification">
              <KioskNotificationBell compact={compact} />
            </div>
          ) : null}
          <LanguageSwitcher compact={compact} />
          {/* Custom right slot (replaces Balance when provided) */}
          {type === 'logged' && rightSlot != null && rightSlot}
          {/* Signup Bonus (KIOSK-317) — next to balance, self-hides for users without a bonus */}
          {type === 'logged' && showBalance && rightSlot == null && (
            <SignupBonusBadge compact={compact} />
          )}
          {/* Balance Display - only when showBalance and no rightSlot */}
          {showBalance && rightSlot == null && (
            <div
              data-tour-id={type === 'logged' ? 'post-login-tour.balance' : undefined}
              className={`relative flex items-center flex-shrink-0 transition-all duration-300 ${
                compact ? 'h-8 px-2 gap-1.5 rounded-[10px]' : 'h-10 px-3 gap-2 rounded-[12px]'
              } ${
                showBalanceAttention ? 'scale-[1.03]' : 'scale-100'
              }`}
              style={{
                display: 'flex',
                alignItems: 'center',
                border: '2px solid transparent',
                background:
                  'linear-gradient(rgba(0,0,0,0.40), rgba(0,0,0,0.40)) padding-box, linear-gradient(135deg, #FFD700, #FF8C00) border-box',
                boxShadow: showBalanceAttention
                  ? '0 0 20px 0 rgba(255, 204, 0, 0.45), 0 0 25px rgba(34, 211, 238, 0.35)'
                  : '0 0 15px 0 rgba(255, 204, 0, 0.18)',
              }}
            >
              {balanceDelta !== null && (
                <span
                  className={`absolute -top-2 -right-2 px-1.5 py-0.5 rounded-md text-[9px] font-black border ${
                    balanceDelta > 0
                      ? 'text-emerald-200 border-emerald-300/50 bg-emerald-500/30'
                      : 'text-red-200 border-red-300/50 bg-red-500/30'
                  } ${showBalanceAttention ? 'animate-pulse' : ''}`}
                >
                  {balanceDelta > 0 ? '+' : '-'}${Math.abs(balanceDelta).toFixed(2)}
                </span>
              )}
              <span className={compact ? 'text-sm' : 'text-base'} aria-hidden>
                💰
              </span>
              {type === 'normal' ? (
                // Always show $0.00 for non-logged users (home page)
                <>
                  <span
                    className={`font-extrabold font-mono ${compact ? 'text-xs' : 'text-base'}`}
                    style={{ color: '#FFD700' }}
                  >
                    $0.00
                  </span>
                  <button
                    onClick={handleWalletClick}
                    className={[
                      'ml-0.5 flex items-center justify-center flex-shrink-0',
                      'text-white/70 hover:text-white transition-transform duration-200 hover:rotate-180',
                      compact ? 'h-4 w-4' : 'h-4 w-4',
                    ].join(' ')}
                    aria-label="Open wallet"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </>
              ) : balanceLoading ? (
                <Loader2
                  className={`animate-spin flex-shrink-0 ${compact ? 'w-4 h-4' : 'w-4 h-4'}`}
                  style={{ color: '#FFD700' }}
                />
              ) : (
                <>
                  <span
                    className={`font-extrabold font-mono ${compact ? 'text-xs' : 'text-base'}`}
                    style={{ color: '#FFD700' }}
                  >
                    ${balance.toFixed(2)}
                  </span>
                  <button
                    onClick={handleReloadBalance}
                    disabled={balanceLoading}
                    className={[
                      'ml-0.5 flex items-center justify-center flex-shrink-0',
                      'text-white/70 hover:text-white disabled:opacity-50',
                      'transition-transform duration-200 hover:rotate-180',
                      compact ? 'h-4 w-4' : 'h-4 w-4',
                      balanceLoading ? 'animate-spin' : '',
                    ].join(' ')}
                    aria-label="Reload balance"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          )}

          {/* User Icon Button - Account Icon - hidden per design */}
          <button
            onClick={handleUserClick}
            className={`hidden bg-white/5 backdrop-blur-md border border-white/20 flex-shrink-0 ${compact ? 'rounded-lg w-7 h-7 sm:w-8 sm:h-8 ml-2 sm:ml-3' : 'rounded-xl w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 ml-4 sm:ml-6 md:ml-8'}`}
          >
            <img
              src="/icons/account-icon.svg"
              alt="Account"
              className={`w-full h-full ${compact ? 'max-w-[14px] max-h-[16px] sm:max-w-[16px] sm:max-h-[18px]' : 'max-w-[20px] max-h-[23px] sm:max-w-[22px] sm:max-h-[25px] md:max-w-[24.02px] md:max-h-[28px]'}`}
              style={{
                objectFit: 'contain'
              }}
            />
          </button>
        </div>
      </div>

      {/* Mobile Title - for smaller sizes - Hidden on mobile to keep single line */}
      {['m', 's', 'xs'].includes(size) && (
        <div className="hidden md:block mt-2 md:mt-3 text-center px-2">
          <h1 className="text-white text-base md:text-lg lg:text-xl font-black italic tracking-wide">
            {type === 'logged'
              ? (title || t('header.accountHub'))
              : t('header.gamingPlatform')}
          </h1>
          <div className="mt-1 md:mt-1.5 flex justify-center">
            <div className="h-0.5 w-16 md:w-20 bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 rounded-full" />
          </div>
          <p className="text-white/60 text-[10px] md:text-xs font-semibold mt-1 md:mt-1.5 tracking-wider">
            {type === 'logged'
              ? (subtitle || t('header.yourLuckyGamingPortal'))
              : t('header.selectPlatformsToDeposit')}
          </p>
        </div>
      )}
    </header>
  );
};

export default Header;
